import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, isTemporaryProviderError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { ArrowLeft, CheckCircle2, Clock, Copy, ExternalLink, KeyRound, Play, RefreshCw, UserPlus, Wallet } from "lucide-react";
import { toast } from "sonner";
import GameActionsBar from "../components/GameActionsBar";
import { siteConfig } from "../lib/siteConfig";
import { BonusBadge, bonusInfoForGame } from "../lib/gameBonus";

export default function GamePlay() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [game, setGame] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [requests, setRequests] = useState([]);
  const [automationAvailability, setAutomationAvailability] = useState(null);
  const [providerBalance, setProviderBalance] = useState(null);
  const [providerRedeemableBalance, setProviderRedeemableBalance] = useState(null);
  const [providerBalanceLoaded, setProviderBalanceLoaded] = useState(false);
  const [gameBonuses, setGameBonuses] = useState({});

  useEffect(() => {
    api.get(`/games/${id}`).then(({ data }) => setGame(data)).catch(() => nav("/games"));
  }, [id, nav]);

  useEffect(() => {
    if (!user || user === false) { setGameBonuses({}); return; }
    api.get("/me/game-bonuses").then(({ data }) => setGameBonuses(data.bonuses || {})).catch(() => setGameBonuses({}));
  }, [user]);

  const loadRequests = useCallback(async () => {
    if (!user || user === false) return;
    const [{ data }, accountsResult] = await Promise.all([
      api.get("/me/requests"),
      api.get("/me/game-accounts").catch(() => ({ data: [] })),
    ]);
    setRequests(mergeRequestRows(Array.isArray(data) ? data : [], Array.isArray(accountsResult.data) ? accountsResult.data : []));
  }, [user]);

  useEffect(() => {
    loadRequests().catch(() => {});
  }, [loadRequests]);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/automation/available-games")
      .then(({ data }) => setAutomationAvailability(data))
      .catch(() => setAutomationAvailability({ attachedGameIds: [], supportedGameIds: [] }));
  }, [user]);

  const gameAutomationId = useMemo(() => automationGameId(game?.name || id), [game?.name, id]);
  const bonusInfo = useMemo(() => bonusInfoForGame(game, gameBonuses), [game, gameBonuses]);
  const gameWithBonus = useMemo(
    () => bonusInfo ? { ...game, current_bonus_percent: bonusInfo.percent } : game,
    [bonusInfo, game],
  );
  const attachedAutomationIds = useMemo(
    () => new Set(automationAvailability?.attachedGameIds || []),
    [automationAvailability],
  );
  const supportedAutomationIds = useMemo(
    () => new Set(automationAvailability?.supportedGameIds || []),
    [automationAvailability],
  );
  const automationLoaded = automationAvailability !== null;
  const automationSupported = supportedAutomationIds.has(gameAutomationId);
  const automationConnected = attachedAutomationIds.has(gameAutomationId);

  const gameRequests = useMemo(
    () => {
      const autoId = automationGameId(game?.name || id);
      return requests.filter((req) => req.game_id === id || req.game_id === autoId);
    },
    [requests, id, game?.name],
  );
  const accountRequest = useMemo(() => latestRequest(gameRequests, "create_account"), [gameRequests]);
  const resetRequest = useMemo(() => latestRequest(gameRequests, "reset_password"), [gameRequests]);
  const accountRequestReusable = useMemo(
    () => isApprovedGameAccountReusable(accountRequest, gameAutomationId),
    [accountRequest, gameAutomationId],
  );
  const createPending = accountRequest?.status === "pending";
  const createApproved = accountRequest?.status === "approved" && accountRequestReusable;
  const showManualAccountPending = createPending && !automationConnected;
  const visibleAccountRequest = automationConnected && (createPending || (accountRequest?.status === "approved" && !accountRequestReusable))
    ? null
    : accountRequest;

  const fetchProviderBalance = useCallback(async () => {
    if (!automationConnected) return null;
    const { data } = await api.post("/automation/balance", { gameName: gameAutomationId }, { skipMaintenance: true });
    const nextBalance = extractBalanceAmount(data);
    const nextRedeemableBalance = extractRedeemableBalanceAmount(data);
    setProviderBalance(nextBalance);
    setProviderRedeemableBalance(nextRedeemableBalance);
    setProviderBalanceLoaded(true);
    return nextBalance;
  }, [automationConnected, gameAutomationId]);

  useEffect(() => {
    if (!user || user === false || !automationConnected || !createApproved) {
      setProviderBalance(null);
      setProviderRedeemableBalance(null);
      setProviderBalanceLoaded(false);
      return;
    }
    let cancelled = false;
    setProviderBalanceLoaded(false);
    api.post("/automation/balance", { gameName: gameAutomationId }, { skipMaintenance: true })
      .then(({ data }) => {
        if (cancelled) return;
        setProviderBalance(extractBalanceAmount(data));
        setProviderRedeemableBalance(extractRedeemableBalanceAmount(data));
        setProviderBalanceLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setProviderBalance(null);
        setProviderRedeemableBalance(null);
        setProviderBalanceLoaded(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user, automationConnected, createApproved, gameAutomationId]);

  const onRefreshBalance = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (automationConnected) {
        await fetchProviderBalance();
        toast.success("Game balance refreshed");
      } else {
        await refreshUser();
        toast.success("Wallet balance refreshed");
      }
    } catch (e) {
      showGamePlayError(e);
    } finally {
      setTimeout(() => setRefreshing(false), 400);
    }
  };

  const onCreateAccount = async () => {
    if (creating) return;
    if (createApproved) {
      toast.info("Account details are shown below");
      return;
    }
    if (!automationConnected) {
      toast.error("Automatic backend is not connected for this game. Attach the provider session first, then create the account.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/automation/create-account", {
        gameName: gameAutomationId,
        username: user.username || user.email,
      });
      await loadRequests();
      await fetchProviderBalance().catch(() => {});
      toast.success("Game account created and saved");
    } catch (e) {
      showGamePlayError(e);
    } finally {
      setCreating(false);
    }
  };

  if (!user) return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold mb-4">Sign in to play</h1>
        <p className="text-white/60 mb-8">You need a {siteConfig.name} account to access this game.</p>
        <Link to="/login" data-testid="play-login-cta" className="px-8 py-3 bg-[#D4AF37] text-black font-bold rounded-full inline-block">Sign in</Link>
      </div>
    </div>
  );

  if (!game) return <div className="min-h-screen bg-[#030705]"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-10 pb-20">
        <button onClick={() => nav(-1)} className="text-sm text-white/50 hover:text-white flex items-center gap-2 mb-6" data-testid="play-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="relative mx-auto w-full max-w-[19rem] overflow-hidden rounded-2xl border border-white/5 bg-black/20 aspect-[5/4] md:max-w-none md:aspect-square">
            {game.image_url ? (
              <img src={game.image_url} alt={game.name} className="h-full w-full object-contain p-3 md:object-cover md:p-0" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#0B2115] to-[#030705]" />
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">{game.category}</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">{game.name}</h1>
            <p className="mt-4 text-white/60">{game.description}</p>
            <div className="mt-6 flex gap-6 text-sm text-white/60">
              <span>RTP <strong className="text-white">{game.rtp}%</strong></span>
              <span>Min <strong className="text-white">{game.min_bet}</strong></span>
              <span>Max <strong className="text-white">{game.max_bet}</strong></span>
            </div>
            {bonusInfo && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <BonusBadge game={game} bonuses={gameBonuses} />
                <span className="text-xs text-white/50">Cash wallet load bonus. Freeplay loads at face value.</span>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {game.play_url ? (
                <a
                  href={game.play_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="play-now-cta"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D4AF37] hover:bg-[#F1C40F] text-black font-bold text-sm tracking-wide transition-all shadow-[0_0_24px_rgba(212,175,55,0.35)]"
                >
                  <Play className="w-4 h-4 fill-current" />
                  Play now
                  <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white/5 border border-white/10 text-white/40 text-sm font-medium" data-testid="play-now-unavailable">
                  <Play className="w-4 h-4" />
                  Play link unavailable
                </span>
              )}

              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#0B2115] border border-[#D4AF37]/30" data-testid="play-balance">
                <Wallet className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-white/50 text-sm">{automationConnected ? "Game balance" : "Wallet"}</span>
                <span className="font-display font-bold tabular-nums text-lg">
                  {automationConnected
                    ? formatBalanceValue(providerBalanceLoaded ? providerBalance : null)
                    : `$${((Number(user?.coins || 0)) / 1000).toFixed(2)}`}
                </span>
              </div>

              <button
                onClick={onRefreshBalance}
                disabled={refreshing}
                data-testid="refresh-balance"
                title="Refresh balance"
                className="inline-flex items-center gap-2 px-4 py-3 rounded-full border border-white/10 hover:border-[#D4AF37]/40 bg-white/[0.03] hover:bg-white/[0.06] text-sm font-medium transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                <span>Refresh balance</span>
              </button>

              <button
                onClick={onCreateAccount}
                disabled={creating || !automationLoaded}
                data-testid="create-game-account"
                title={!automationLoaded ? "Checking backend connection" : !automationConnected ? "Backend session required" : "Create game account"}
                className={`inline-flex items-center gap-2 px-4 py-3 rounded-full border text-sm font-medium transition-all disabled:opacity-50 ${
                  automationConnected
                    ? "border-emerald-500/30 hover:border-emerald-400/60 bg-emerald-900/10 hover:bg-emerald-900/20 text-emerald-300"
                    : "border-[#D4AF37]/35 hover:border-[#D4AF37]/70 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/15 text-[#D4AF37]"
                }`}
              >
                <UserPlus className="w-4 h-4" />
                <span>{creating ? "Submitting..." : createApproved ? "Account details" : !automationLoaded ? "Checking..." : !automationConnected ? "Backend required" : "Create account"}</span>
              </button>
            </div>
            {automationLoaded && !automationConnected && (
              <div className="mt-3 rounded-2xl border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-4 py-3 text-xs text-[#f5d76e]">
                {automationSupported
                  ? "Automatic backend needs a fresh provider session before account create, load, or redeem can run."
                  : "This game is not enabled for automatic backend yet."}
              </div>
            )}
          </div>
        </div>

        {/* Account actions */}
        <div className="mt-10 md:mt-12 rounded-3xl border border-white/5 bg-[#0A130E]/60 p-5 md:p-8">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Account actions</div>
              <h3 className="font-display text-xl font-bold">Manage your play</h3>
            </div>
          </div>
          {(visibleAccountRequest || resetRequest) && (
            <RequestDetailsPanel
              accountPendingText={
                automationConnected
                  ? "Automatic creation is available. Tap Create now above to finish this game account."
                  : "Automatic backend is not connected for this game yet."
              }
              accountRequest={visibleAccountRequest}
              resetRequest={resetRequest}
            />
          )}
          <GameActionsBar
            gameId={id}
            game={gameWithBonus}
            automationConnected={automationConnected}
            automationSupported={automationSupported}
            hasGameAccount={createApproved}
            onNeedAccount={onCreateAccount}
            providerBalance={providerBalance}
            providerRedeemableBalance={providerRedeemableBalance}
            providerBalanceLoaded={providerBalanceLoaded}
            onLoadComplete={async () => {
              await refreshUser();
              await fetchProviderBalance().catch(() => {});
            }}
            onRequestCreated={(req) => {
              setRequests((current) => [req, ...current]);
              loadRequests().catch(() => {});
            }}
          />
        </div>
      </div>
    </div>
  );
}

function latestRequest(rows, type) {
  const typed = rows.filter((req) => req.type === type && !isArchivedRequestStatus(req.status));
  return typed.find((req) => req.status === "approved" && req.synthetic_game_account)
    || typed.find((req) => req.status === "approved")
    || typed.find((req) => req.status === "pending")
    || typed[0]
    || null;
}

function mergeRequestRows(primaryRows, accountRows) {
  const merged = [];
  const seen = new Set();
  const keyFor = (row) => [
    row?.type || "",
    automationGameId(row?.game_id || row?.gameName || ""),
    row?.synthetic_game_account ? "saved" : row?.id || "",
  ].join(":");
  for (const row of [...accountRows, ...primaryRows]) {
    const key = keyFor(row);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }
  return merged;
}

function isArchivedRequestStatus(status = "") {
  return ["cancelled", "canceled", "superseded", "expired"].includes(String(status).toLowerCase());
}

function showGamePlayError(error) {
  const message = formatApiError(error, { audience: "player" });
  if (isTemporaryProviderError(error)) {
    toast.warning(message);
    return;
  }
  toast.error(message);
}

function extractBalanceAmount(payload) {
  const candidates = [
    payload?.balance?.balance,
    payload?.balance?.user_balance,
    payload?.balance?.userBalance,
    payload?.balance?.score,
    payload?.balance?.data?.user_balance,
    payload?.balance?.data?.balance,
    payload?.balance?.response?.data?.user_balance,
    payload?.balance?.response?.data?.balance,
    payload?.response?.data?.user_balance,
    payload?.response?.data?.balance,
    payload?.data?.user_balance,
    payload?.data?.balance,
    payload?.user_balance,
    payload?.userBalance,
    payload?.score,
    payload?.balance,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "number" || typeof value === "string") return value;
  }
  return null;
}

function extractRedeemableBalanceAmount(payload) {
  const candidates = [
    payload?.redeemable_balance,
    payload?.balance?.redeemable_balance,
    payload?.balance?.total_wins,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    if (typeof value === "number" || typeof value === "string") return value;
  }
  return null;
}

function formatBalanceValue(value) {
  if (value === null || value === undefined || value === "") return "--";
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return `$${numeric.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(value);
}

function RequestDetailsPanel({ accountPendingText, accountRequest, resetRequest }) {
  return (
    <div className="mb-5 grid md:grid-cols-2 gap-3">
      <RequestCard
        icon={UserPlus}
        title="Game account"
        request={accountRequest}
        pendingText={accountPendingText}
        approvedTitle="Account details"
        emptyText="Username/password not added yet. Admin can edit this request from Requests."
      />
      <RequestCard
        icon={KeyRound}
        title="Password reset"
        request={resetRequest}
        pendingText="Password reset is processing. New password will show here when automation finishes."
        approvedTitle="New password"
        emptyText="New password not added yet. Try reset again or contact support if it stays processing."
      />
    </div>
  );
}

function RequestCard({ icon: Icon, title, request, pendingText, approvedTitle, emptyText }) {
  if (!request) return null;
  const status = request.status || "pending";
  const approved = status === "approved";
  const rejected = status === "rejected";
  const detail = (request.admin_note || "").trim();
  const parsed = parseDetails(detail);

  return (
    <div className="rounded-2xl border border-white/10 bg-black/25 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/10 grid place-items-center shrink-0">
            <Icon className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm truncate">{title}</div>
            <div className="text-[11px] text-white/40">{formatRequestDate(request.created_at)}</div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] border capitalize ${statusTone(status)}`}>
          {approved ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {status.replace("_", " ")}
        </span>
      </div>

      {approved ? (
        detail || request.type === "create_account" || request.type === "reset_password" ? (
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-white/40 mb-2">{approvedTitle}</div>
            <div className="rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/10 p-3">
              {request.type === "create_account" ? (
                <div className="space-y-2">
                  <CredentialLine label="Username" value={parsed.username || ""} />
                  <CredentialLine label="Password" value={parsed.password || ""} />
                  {parsed.notes && <CredentialLine label="Notes" value={parsed.notes} />}
                  <p className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs leading-relaxed text-amber-100">
                    The live balance belongs to this exact game account. If the game app shows a different balance, sign out there and log in again with these details.
                  </p>
                </div>
              ) : request.type === "reset_password" ? (
                <CredentialLine label="Password" value={parsed.password || detail} />
              ) : (
                <pre className="whitespace-pre-wrap break-words text-sm text-[#F8E08E] font-sans leading-relaxed">{detail}</pre>
              )}
              <button
                type="button"
                onClick={() => copyDetail(detail)}
                disabled={!detail}
                className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 hover:bg-black/50 text-[#D4AF37] text-xs disabled:opacity-40"
                title="Copy"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-white/55">{emptyText}</p>
        )
      ) : rejected ? (
        <p className="text-sm text-red-300">{detail || "Request rejected. Contact support for help."}</p>
      ) : (
        <p className="text-sm text-white/55">{pendingText}</p>
      )}
    </div>
  );
}

function CredentialLine({ label, value }) {
  return (
    <div className="grid grid-cols-[92px_minmax(0,1fr)] gap-3 items-start text-sm">
      <span className="text-white/45">{label}</span>
      <span className={`font-semibold break-words ${value ? "text-[#F8E08E]" : "text-white/30 italic"}`}>
        {value || "Not added yet"}
      </span>
    </div>
  );
}

function parseDetails(text = "") {
  const out = {};
  text.split(/\r?\n/).forEach((line) => {
    const [rawKey, ...rest] = line.split(":");
    const key = rawKey.trim().toLowerCase();
    const value = rest.join(":").trim();
    if (!value) return;
    if (["username", "user", "login"].includes(key)) out.username = value;
    if (["password", "pass", "new password"].includes(key)) out.password = value;
    if (["notes", "note"].includes(key)) out.notes = value;
  });
  return out;
}

function isApprovedGameAccountReusable(request, gameAutomationId) {
  if (!request || request.status !== "approved") return false;
  if (gameAutomationId === "riversweeps") return true;
  const parsed = parseDetails(request.admin_note || "");
  const username = (parsed.username || "").trim();
  const password = (parsed.password || "").trim();
  return Boolean(username && password);
}

function statusTone(status) {
  if (status === "approved") return "bg-emerald-900/25 border-emerald-600/30 text-emerald-300";
  if (status === "rejected") return "bg-red-900/25 border-red-600/30 text-red-300";
  return "bg-slate-900/60 border-white/10 text-slate-300";
}

function formatRequestDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

async function copyDetail(value) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy");
  }
}

function automationGameId(value = "") {
  const key = String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
  const map = {
    juwa: "juwa",
    juwa20: "juwa2",
    juwa2: "juwa2",
    riversweeps: "riversweeps",
    vegassweeps: "lasvegassweeps",
    lasvegassweeps: "lasvegassweeps",
    firekirin: "firekirin",
    gamevault: "gamevault",
    pandamaster: "pandamaster",
    orionstars: "orionstars",
    mafia: "mafia",
    cashfrenzy: "cashfrenzy",
    mrallinone: "mrallinone",
    yolo: "yolo",
    cashmachine: "cashmachine",
    vblink: "vblink",
    milkyways: "milkyway",
    milkyway: "milkyway",
    gameroom: "gameroom",
    ultrapanda: "ultrapanda",
    bluedragon: "bluedragon",
    noble777: "noble777",
    vegasroll: "vegasroll",
    winstar: "winstar",
  };
  return map[key] || key;
}
